<script>
  import { token } from "./store/token.js";
  import { permissions } from "./store/permissions.js";
  import { create_impersonation_token } from "./api/request.js";
  import { onMount, createEventDispatcher } from "svelte";
  export let userId = null;

  const dispatch = createEventDispatcher();

  // Portal action: moves a node to document.body so fixed-positioned modals
  // escape the drawer's stacking/overflow context in UserListV2.
  const portal = (node, target = document.body) => {
    target.appendChild(node);
    return {
      destroy() {
        if (node.parentNode) node.parentNode.removeChild(node);
      },
    };
  };

  // Impersonation modal state
  let showImpersonationModal = false;
  let impersonating = false;
  let impersonationToken = "";
  let impersonationError = "";
  let tokenCopied = false;

  $: canImpersonate = $permissions.role === "admin";

  const handleImpersonate = async () => {
    if (!userId || impersonating) return;
    impersonating = true;
    impersonationError = "";
    impersonationToken = "";
    tokenCopied = false;
    showImpersonationModal = true;
    try {
      const result = await create_impersonation_token(userId);
      impersonationToken = result.token;
    } catch (e) {
      impersonationError = e.message;
    } finally {
      impersonating = false;
    }
  };

  const closeImpersonationModal = () => {
    showImpersonationModal = false;
    impersonationToken = "";
    impersonationError = "";
    tokenCopied = false;
  };

  const copyTokenToClipboard = async () => {
    if (!impersonationToken) return;
    try {
      await navigator.clipboard.writeText(impersonationToken);
      tokenCopied = true;
      setTimeout(() => (tokenCopied = false), 2000);
    } catch (e) {
      impersonationError = "Failed to copy: " + e.message;
    }
  };

  const launchAsUser = () => {
    if (!impersonationToken) return;
    const url = `https://app.vollume.com/authed?event=signedin&auth_token=${encodeURIComponent(impersonationToken)}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  let user = null;
  let workspaceList = [];
  let perks = [];
  let devices = [];
  let onboarding = [];
  let completedOnboarding = [];
  let activityLog = [];
  let lastActivityDate = null;
  let desktopUsed = false;
  let mobileUsed = false;
  let loading = true;
  let error = null;
  let workspaceSongs = new Map(); // Map to store songs for each workspace
  let loadingSongs = new Set(); // Set to track which workspaces are loading songs
  let expandedWorkspaces = new Set(); // Set to track expanded/collapsed state

  // For compact device list
  let showAllDevices = false;
  const maxDevicesToShow = 5;

  // Tabbed layout
  let activeTab = "overview";
  const TABS = [
    { id: "overview", label: "Overview" },
    { id: "workspaces", label: "Workspaces & Songs" },
    { id: "devices", label: "Devices" },
    { id: "activity", label: "Activity" },
  ];

  const fetchUserDetail = async () => {
    if (!userId) return;
    try {
      loading = true;
      error = null;
      const response = await fetch(
        `${process.env.API_ROOT}/admin/user/${userId}/detail`,
        {
          headers: {
            Authorization: "Bearer " + $token,
          },
        },
      );
      if (!response.ok)
        throw new Error(`HTTP error! status: ${response.status}`);
      const data = await response.json();
      user = data.user;
      workspaceList = data.workspace_list || [];
      perks = data.perk_list || [];
      devices = data.connected_devices || [];
      onboarding = data.available_onboarding_steps || [];
      completedOnboarding = data.completed_onboarding_steps || [];
      activityLog = data.activity_log || [];
      lastActivityDate = data.last_activity_date;
      desktopUsed = data.desktop_used;
      mobileUsed = data.mobile_used;
    } catch (e) {
      error = e.message;
    } finally {
      loading = false;
    }
  };

  onMount(fetchUserDetail);

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Platform icon helper
  function getPlatformIcon(platform, type) {
    if (!platform && type) platform = type;
    platform = (platform || "").toLowerCase();
    if (platform.includes("mac")) return "🍎"; // macOS
    if (platform.includes("ios")) return "📱"; // iOS
    if (platform.includes("android")) return "🤖"; // Android
    if (platform.includes("win")) return "🪟"; // Windows
    if (platform.includes("linux")) return "🐧"; // Linux
    if (platform.includes("browser") || type === "browser") return "🌐"; // Browser
    return "💻"; // Default
  }

  // Workspace click handler
  function handleWorkspaceClick(ws) {
    dispatch("openWorkspace", { workspaceId: ws.string_id });
  }

  // Onboarding check helper
  function isStepCompleted(stepId) {
    return completedOnboarding.some((s) => s.step_id === stepId);
  }

  // Helper function to get highest role from role_list
  function getHighestRole(roleList) {
    if (!roleList || !roleList.length) return null;
    // Sort by role_level in descending order and return the first role
    return roleList.sort((a, b) => b.role_level - a.role_level)[0].role_name;
  }

  const fetchWorkspaceSongs = async (workspaceId) => {
    if (!userId || !workspaceId) return;
    try {
      loadingSongs = new Set(loadingSongs.add(workspaceId));
      const response = await fetch(
        `${process.env.API_ROOT}/admin/user/${userId}/workspace/${workspaceId}/song/list`,
        {
          headers: {
            Authorization: "Bearer " + $token,
          },
        },
      );
      if (!response.ok)
        throw new Error(`HTTP error! status: ${response.status}`);
      const data = await response.json();
      workspaceSongs = new Map(
        workspaceSongs.set(workspaceId, data.records || []),
      );
    } catch (e) {
      console.error(`Error fetching songs for workspace ${workspaceId}:`, e);
      workspaceSongs = new Map(workspaceSongs.set(workspaceId, []));
    } finally {
      const updatedLoadingSongs = new Set(loadingSongs);
      updatedLoadingSongs.delete(workspaceId);
      loadingSongs = updatedLoadingSongs;
    }
  };

  // Only fetch songs when workspace is expanded
  function toggleWorkspaceSongs(ws) {
    if (expandedWorkspaces.has(ws.string_id)) {
      // Collapse
      expandedWorkspaces = new Set(
        [...expandedWorkspaces].filter((id) => id !== ws.string_id),
      );
    } else {
      // Expand
      expandedWorkspaces = new Set(expandedWorkspaces.add(ws.string_id));
      if (!workspaceSongs.has(ws.string_id)) {
        fetchWorkspaceSongs(ws.string_id);
      }
    }
  }

  const formatDuration = (seconds) => {
    if (!seconds) return "-";
    const minutes = Math.floor(seconds);
    const remainingSeconds = Math.floor((seconds % 1) * 60);
    return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`;
  };
</script>

<div class="user-detail-container">
  {#if loading}
    <div class="loading">Loading user details...</div>
  {:else if error}
    <div class="error">{error}</div>
  {:else if user}
    <!-- Sticky identity header -->
    <header class="ud-header">
      <div class="ud-identity">
        <div class="ud-avatar">
          {#if user.avatar_url}
            <img src={user.avatar_url} alt={user.name} />
          {:else}
            <div class="avatar-placeholder">
              {user.name && user.name.length > 0 ? user.name.charAt(0) : "?"}
            </div>
          {/if}
        </div>
        <div class="ud-identity-text">
          <div class="ud-name">{user.name || "Unnamed User"}</div>
          <div class="ud-email">{user.email}</div>
          <div class="ud-chips">
            {#if user.artist_name}
              <span class="chip chip-artist">🎵 {user.artist_name}</span>
            {/if}
            <span
              class="chip"
              class:chip-ok={user.is_verified}
              class:chip-warn={!user.is_verified}
            >
              {user.is_verified ? "✓ Verified" : "Unverified"}
            </span>
            {#if desktopUsed}<span class="chip">Desktop</span>{/if}
            {#if mobileUsed}<span class="chip">Mobile</span>{/if}
            {#if user.website}
              <a class="chip chip-link" href={user.website} target="_blank"
                >🔗 Website</a
              >
            {/if}
          </div>
        </div>
      </div>
      {#if canImpersonate}
        <div class="ud-actions">
          <button
            class="impersonate-btn"
            on:click={handleImpersonate}
            disabled={impersonating}
            title="Generate a read-only impersonation token for this user"
          >
            {impersonating ? "Generating..." : "🎭 Impersonate User"}
          </button>
        </div>
      {/if}
    </header>

    <!-- Tabs -->
    <nav class="ud-tabs">
      {#each TABS as tab}
        <button
          class="ud-tab"
          class:active={activeTab === tab.id}
          on:click={() => (activeTab = tab.id)}
        >
          {tab.label}
          {#if tab.id === "workspaces"}
            <span class="tab-count">{workspaceList.length}</span>
          {:else if tab.id === "devices"}
            <span class="tab-count">{devices.length}</span>
          {:else if tab.id === "activity"}
            <span class="tab-count">{activityLog.length}</span>
          {/if}
        </button>
      {/each}
    </nav>

    <!-- ===== Overview tab ===== -->
    {#if activeTab === "overview"}
    <div class="main-grid">
      <div class="card">
        <h2>Details</h2>
        <div class="detail-rows">
          <div class="d-row">
            <span class="d-label">User ID</span>
            <span class="d-val">{user.user_id || userId}</span>
          </div>
          <div class="d-row">
            <span class="d-label">Artist Name</span>
            <span class="d-val">{user.artist_name || "-"}</span>
          </div>
          {#if user.occupation}
            <div class="d-row">
              <span class="d-label">Occupation</span>
              <span class="d-val">{user.occupation}</span>
            </div>
          {/if}
          <div class="d-row">
            <span class="d-label">Created</span>
            <span class="d-val">{formatDate(user.created_at)}</span>
          </div>
          <div class="d-row">
            <span class="d-label">Last Login</span>
            <span class="d-val"
              >{user.last_login ? formatDate(user.last_login) : "Never"}</span
            >
          </div>
          {#if lastActivityDate}
            <div class="d-row">
              <span class="d-label">Last Activity</span>
              <span class="d-val">{formatDate(lastActivityDate)}</span>
            </div>
          {/if}
          <div class="d-row">
            <span class="d-label">Verified</span>
            <span class="d-val">{user.is_verified ? "Yes" : "No"}</span>
          </div>
        </div>
        {#if user.bio}
          <div class="ud-bio">{user.bio}</div>
        {/if}
      </div>
      <div class="card">
        <h2>Perks</h2>
        {#if perks.length > 0}
          <ul class="perk-list">
            {#each perks as perk}
              <li>{perk.name} <span class="perk-val">{perk.val}</span></li>
            {/each}
          </ul>
        {:else}
          <div class="empty">No perks</div>
        {/if}
      </div>
      <div class="card">
        <h2>Onboarding Steps</h2>
        {#if onboarding.length > 0}
          <ul class="onboarding-list">
            {#each onboarding as step}
              <li>
                <span class="onboarding-check"
                  >{isStepCompleted(step.step_id) ? "✔️" : "○"}</span
                >
                <span>{step.title}</span>
              </li>
            {/each}
          </ul>
        {:else}
          <div class="empty">No onboarding steps</div>
        {/if}
      </div>
    </div>
    {/if}

    <!-- ===== Workspaces & Songs tab ===== -->
    {#if activeTab === "workspaces"}
    <div class="card">
      <h2>Workspaces</h2>
      {#if workspaceList.length > 0}
        <div class="workspace-list">
          {#each workspaceList as ws}
            <div
              class="workspace-item workspace-link"
              on:click={() => handleWorkspaceClick(ws)}
              tabindex="0"
              title="Open workspace detail"
            >
              {#if ws.image_url}
                <img src={ws.image_url} alt={ws.title} class="workspace-img" />
              {/if}
              <div class="workspace-info">
                <div class="workspace-title">{ws.title}</div>
                <div class="workspace-meta">
                  {#if ws.role_list && ws.role_list.length > 0}
                    <span
                      class="role-badge"
                      class:badge-owner={getHighestRole(ws.role_list) ===
                        "owner"}
                      class:badge-viewer={getHighestRole(ws.role_list) ===
                        "everyone"}
                    >
                      {getHighestRole(ws.role_list).charAt(0).toUpperCase() +
                        getHighestRole(ws.role_list).slice(1)}
                    </span>
                  {:else}
                    <span class="role-badge">Member</span>
                  {/if}
                  <span class="workspace-date">{formatDate(ws.created_at)}</span>
                </div>
              </div>
            </div>
          {/each}
        </div>
      {:else}
        <div class="empty">No workspaces found</div>
      {/if}
    </div>
    <div class="card">
      <h2>Workspace Songs</h2>
      {#if workspaceList.length > 0}
        {#each workspaceList as ws}
          <div class="workspace-songs-section">
            <h3 class="workspace-songs-title">
              {ws.title}
              <button
                class="expand-btn"
                on:click={() => toggleWorkspaceSongs(ws)}
              >
                {expandedWorkspaces.has(ws.string_id)
                  ? "Hide Songs"
                  : "Show Songs"}
              </button>
              {#if expandedWorkspaces.has(ws.string_id)}
                {#if loadingSongs.has(ws.string_id)}
                  <span class="loading-indicator">Loading...</span>
                {:else if workspaceSongs.has(ws.string_id)}
                  <span class="song-count"
                    >({workspaceSongs.get(ws.string_id).length} songs)</span
                  >
                {/if}
              {/if}
            </h3>
            {#if expandedWorkspaces.has(ws.string_id)}
              {#if workspaceSongs.has(ws.string_id)}
                {#if workspaceSongs.get(ws.string_id).length > 0}
                  <div class="song-list">
                    {#each workspaceSongs.get(ws.string_id) as song}
                      <div class="song-item">
                        <div class="song-info">
                          <div class="song-title">
                            {song.title || song.original_filename}
                          </div>
                          <div class="song-meta">
                            {#if song.metadata_artist}
                              <span class="song-artist"
                                >{song.metadata_artist}</span
                              >
                            {/if}
                            {#if song.metadata_album}
                              <span class="song-album"
                                >{song.metadata_album}</span
                              >
                            {/if}
                            <span class="song-duration"
                              >{formatDuration(
                                song.audio_version_duration,
                              )}</span
                            >
                            <span class="song-date"
                              >{formatDate(song.created_at)}</span
                            >
                          </div>
                        </div>
                        <div class="song-uploader">
                          {#if song.uploader}
                            <span class="uploader-name"
                              >Uploaded by {song.uploader.name}</span
                            >
                          {/if}
                        </div>
                      </div>
                    {/each}
                  </div>
                {:else}
                  <div class="empty">No songs in this workspace</div>
                {/if}
              {:else}
                <div class="empty">Loading songs...</div>
              {/if}
            {/if}
          </div>
        {/each}
      {:else}
        <div class="empty">No workspaces found</div>
      {/if}
    </div>
    {/if}

    <!-- ===== Devices tab ===== -->
    {#if activeTab === "devices"}
    <div class="card">
      <h2>Connected Devices</h2>
      {#if devices.length > 0}
        <ul class="device-list compact">
          {#each showAllDevices ? devices : devices.slice(0, maxDevicesToShow) as device}
            <li>
              <span class="platform-icon"
                >{getPlatformIcon(
                  device.device_platform,
                  device.device_type,
                )}</span
              >
              <span class="device-name"
                >{device.device_name || device.device_type || "Unknown"}</span
              >
              <span class="device-location">{device.location}</span>
              <span class="device-date">{formatDate(device.created_at)}</span>
            </li>
          {/each}
        </ul>
        {#if devices.length > maxDevicesToShow}
          <button
            class="show-all-btn"
            on:click={() => (showAllDevices = !showAllDevices)}
          >
            {showAllDevices ? "Show Less" : `Show All (${devices.length})`}
          </button>
        {/if}
      {:else}
        <div class="empty">No devices</div>
      {/if}
    </div>
    {/if}

    <!-- ===== Activity tab ===== -->
    {#if activeTab === "activity"}
    <div class="card">
      <h2>Activity Log</h2>
      {#if activityLog.length > 0}
        <div class="activity-log-list">
          {#each activityLog as log}
            <div class="activity-item">
              <div class="activity-main">
                <div class="activity-date">{formatDate(log.created_at)}</div>
                <div class="activity-device">
                  <span class="platform-icon"
                    >{getPlatformIcon(
                      log.device_platform,
                      log.device_type,
                    )}</span
                  >
                  <span class="device-text">
                    {log.device_name || "Unknown Device"}
                    {#if log.device_platform}
                      <span class="device-sub">({log.device_platform})</span>
                    {/if}
                  </span>
                </div>
              </div>
              <div class="activity-meta">
                {#if log.location || log.ip}
                  <div class="activity-location">
                    {log.location || "Unknown Location"}
                    {#if log.ip}<span class="ip-address">({log.ip})</span>{/if}
                  </div>
                {/if}
                {#if log.env}
                  <div class="activity-env">
                    <span class="env-badge {log.env}">{log.env}</span>
                  </div>
                {/if}
              </div>
            </div>
          {/each}
        </div>
      {:else}
        <div class="empty">No activity log</div>
      {/if}
    </div>
    {/if}
  {:else}
    <div class="empty">User not found</div>
  {/if}

  {#if showImpersonationModal}
    <div class="modal-overlay" use:portal on:click={closeImpersonationModal}>
      <div class="impersonation-modal" on:click|stopPropagation>
        <button class="modal-close" on:click={closeImpersonationModal}>&times;</button>
        <h2>Impersonation Token</h2>
        {#if user}
          <p class="modal-subtitle">
            For <strong>{user.name || user.email}</strong>
          </p>
        {/if}

        {#if impersonating}
          <div class="modal-loading">Generating token...</div>
        {:else if impersonationError}
          <div class="modal-error">{impersonationError}</div>
        {:else if impersonationToken}
          <div class="readonly-warning">
            ⚠️ This token is <strong>read-only</strong>. Mutating requests (POST/PUT/PATCH/DELETE) will be rejected with 403.
          </div>

          <label class="token-label" for="impersonation-token">JWT Token</label>
          <textarea
            id="impersonation-token"
            class="token-field"
            readonly
            rows="4"
            on:click={(e) => e.target.select()}
            value={impersonationToken}
          ></textarea>

          <div class="modal-actions">
            <button class="btn-cancel" on:click={closeImpersonationModal}>Close</button>
            <button class="btn-submit" on:click={copyTokenToClipboard}>
              {tokenCopied ? "✓ Copied" : "Copy Token"}
            </button>
            <button class="btn-submit" on:click={launchAsUser}>
              Launch as User →
            </button>
          </div>
        {/if}
      </div>
    </div>
  {/if}
</div>

<style>
  .user-detail-container {
    padding: 0 0 2.5rem;
    max-width: 1200px;
    margin: 0 auto;
  }
  .card {
    background: #fff;
    border-radius: 16px;
    padding: 1.5rem;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
    border: 1px solid #eef0f3;
    min-width: 0;
    margin-bottom: 1.5rem;
  }
  .card h2 {
    font-size: 1rem;
    font-weight: 700;
    color: #111827;
    margin: 0 0 1rem;
    padding-bottom: 0.6rem;
    border-bottom: 1px solid #f1f5f9;
  }
  /* ===== Sticky header ===== */
  .ud-header {
    position: sticky;
    top: 0;
    z-index: 20;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1.5rem;
    background: rgba(255, 255, 255, 0.92);
    backdrop-filter: saturate(180%) blur(8px);
    border: 1px solid #eef0f3;
    border-radius: 16px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
    padding: 1rem 1.5rem;
    margin-bottom: 1.25rem;
  }
  .ud-identity {
    display: flex;
    align-items: center;
    gap: 1.25rem;
    min-width: 0;
  }
  .ud-avatar {
    width: 64px;
    height: 64px;
    border-radius: 14px;
    overflow: hidden;
    background: #f1f5f9;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }
  .ud-avatar img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
  .ud-identity-text {
    min-width: 0;
  }
  .ud-name {
    font-weight: 700;
    font-size: 1.35rem;
    color: #111827;
    line-height: 1.2;
  }
  .ud-email {
    color: #6b7280;
    font-size: 0.95rem;
    margin-top: 0.1rem;
  }
  .ud-chips {
    display: flex;
    flex-wrap: wrap;
    gap: 0.4rem;
    margin-top: 0.5rem;
  }
  .chip {
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
    background: #eef2f7;
    color: #475569;
    border-radius: 9999px;
    padding: 0.2rem 0.65rem;
    font-size: 0.78rem;
    font-weight: 600;
    text-decoration: none;
  }
  .chip-ok {
    background: #e6f9ed;
    color: #16a34a;
  }
  .chip-warn {
    background: #fef3c7;
    color: #b45309;
  }
  .chip-artist {
    background: #ede9fe;
    color: #6d28d9;
  }
  .chip-link {
    background: #e0edff;
    color: #2563eb;
  }
  .ud-actions {
    flex-shrink: 0;
  }

  /* ===== Tabs ===== */
  .ud-tabs {
    display: flex;
    gap: 0.25rem;
    border-bottom: 1px solid #e5e7eb;
    margin-bottom: 1.75rem;
    overflow-x: auto;
  }
  .ud-tab {
    appearance: none;
    background: none;
    border: none;
    border-bottom: 2px solid transparent;
    padding: 0.7rem 1rem;
    font-size: 0.95rem;
    font-weight: 600;
    color: #6b7280;
    cursor: pointer;
    white-space: nowrap;
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    transition: color 0.15s, border-color 0.15s;
  }
  .ud-tab:hover {
    color: #111827;
  }
  .ud-tab:focus {
    outline: none;
  }
  .ud-tab:focus-visible {
    outline: 2px solid #93c5fd;
    outline-offset: -2px;
    border-radius: 6px;
  }
  .ud-tab.active {
    color: #2563eb;
    border-bottom-color: #2563eb;
  }
  .tab-count {
    background: #eef2f7;
    color: #64748b;
    border-radius: 9999px;
    font-size: 0.72rem;
    font-weight: 700;
    padding: 0.05rem 0.45rem;
    min-width: 1.25rem;
    text-align: center;
  }
  .ud-tab.active .tab-count {
    background: #dbeafe;
    color: #2563eb;
  }

  /* ===== Details rows ===== */
  .detail-rows {
    display: flex;
    flex-direction: column;
  }
  .d-row {
    display: flex;
    justify-content: space-between;
    gap: 1rem;
    padding: 0.55rem 0;
    border-bottom: 1px solid #f1f5f9;
    font-size: 0.95rem;
  }
  .d-row:last-child {
    border-bottom: none;
  }
  .d-label {
    color: #6b7280;
    font-weight: 500;
  }
  .d-val {
    color: #111827;
    font-weight: 600;
    text-align: right;
    word-break: break-word;
  }
  .d-val a {
    color: #2563eb;
  }
  .ud-bio {
    margin-top: 1rem;
    color: #475569;
    font-size: 0.92rem;
    line-height: 1.5;
    white-space: pre-wrap;
  }
  .avatar-placeholder {
    width: 100%;
    height: 100%;
    color: #4a5568;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 2rem;
    font-weight: 600;
  }
  .user-info {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 0.2rem;
  }
  .user-name {
    font-weight: 700;
    font-size: 1.5rem;
    color: #222c3a;
  }
  .user-email {
    color: #7b8794;
    font-size: 1.1rem;
  }
  .user-artist,
  .user-occupation,
  .user-bio,
  .user-website,
  .user-last-activity {
    color: #4a5568;
    font-size: 1.05rem;
  }
  .user-website a {
    color: #2563eb;
    text-decoration: underline;
  }
  .user-meta-row {
    margin-top: 0.5rem;
    display: flex;
    gap: 1.5rem;
    align-items: center;
    flex-wrap: wrap;
    color: #4a5568;
    font-size: 1.02rem;
  }
  .device-badge {
    background: #e2e8f0;
    color: #7b8794;
    border-radius: 9999px;
    padding: 0.2rem 0.9rem;
    font-size: 0.98rem;
    font-weight: 500;
    margin-left: 0.2rem;
  }
  .device-badge.active {
    background: #e6f9ed;
    color: #2ecc71;
  }
  .main-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
    gap: 2rem;
  }
  .workspace-list {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }
  .workspace-item {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    background: #f7f9fb;
    border-radius: 8px;
    padding: 0.75rem 1rem;
  }
  .workspace-link {
    cursor: pointer;
    transition: background 0.15s;
  }
  .workspace-link:hover,
  .workspace-link:focus {
    background: #e6f0fa;
    outline: none;
  }
  .workspace-img {
    width: 36px;
    height: 36px;
    border-radius: 8px;
    object-fit: cover;
    background: #e2e8f0;
  }
  .workspace-info {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
  }
  .workspace-meta {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    color: #7b8794;
    font-size: 0.95rem;
  }
  .role-badge {
    padding: 0.2rem 0.75rem;
    border-radius: 9999px;
    font-size: 0.9rem;
    font-weight: 500;
    background: #e2e8f0;
    color: #4a5568;
    text-transform: capitalize;
  }
  .role-badge.badge-owner {
    background: #e6f0fa;
    color: #2563eb;
  }
  .role-badge.badge-viewer {
    background: #f7fafc;
    color: #4a5568;
  }
  .workspace-title {
    font-weight: 500;
    color: #2d3748;
  }
  .workspace-date {
    color: #7b8794;
    font-size: 0.95rem;
    margin-left: auto;
  }
  .perk-list,
  .device-list,
  .onboarding-list {
    margin: 0;
    padding: 0 0 0 1.2rem;
    color: #4a5568;
    font-size: 1rem;
  }
  .perk-val {
    color: #2563eb;
    margin-left: 0.5rem;
  }
  .device-list.compact {
    padding-left: 0;
  }
  .device-list.compact li {
    display: flex;
    align-items: center;
    gap: 0.7rem;
    padding: 0.3rem 0.1rem;
    font-size: 0.98rem;
  }
  .platform-icon {
    font-size: 1.2rem;
    margin-right: 0.2rem;
  }
  .device-name {
    font-weight: 500;
    color: #2d3748;
  }
  .device-location {
    color: #7b8794;
    font-size: 0.97rem;
  }
  .device-date {
    color: #a0aec0;
    font-size: 0.93rem;
    margin-left: auto;
  }
  .show-all-btn {
    margin-top: 0.5rem;
    background: #f1f5f9;
    color: #2563eb;
    border: none;
    border-radius: 6px;
    padding: 0.4rem 1.1rem;
    font-size: 1rem;
    cursor: pointer;
    transition: background 0.15s;
  }
  .show-all-btn:hover {
    background: #e6f0fa;
  }
  .onboarding-list {
    list-style: none;
    padding-left: 0;
  }
  .onboarding-list li {
    display: flex;
    align-items: center;
    gap: 0.7rem;
    font-size: 1.05rem;
    padding: 0.2rem 0;
  }
  .onboarding-check {
    font-size: 1.2rem;
    width: 1.5em;
    display: inline-block;
    text-align: center;
  }
  .onboarding-list.completed li {
    text-decoration: none;
    color: inherit;
  }
  .loading,
  .error,
  .empty {
    color: #7b8794;
    text-align: center;
    padding: 2rem 0;
  }
  .activity-log-list {
    display: flex;
    flex-direction: column;
  }
  .activity-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1rem 0;
    border-bottom: 1px solid #e2e8f0;
  }
  .activity-item:last-child {
    border-bottom: none;
  }
  .activity-main {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
  }
  .activity-date {
    font-size: 0.9rem;
    color: #7b8794;
  }
  .activity-device {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-weight: 500;
    color: #2d3748;
  }
  .device-sub {
    font-weight: normal;
    color: #7b8794;
    font-size: 0.9rem;
  }
  .activity-meta {
    text-align: right;
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
    align-items: flex-end;
  }
  .activity-location {
    font-size: 0.9rem;
    color: #4a5568;
  }
  .ip-address {
    color: #a0aec0;
    font-size: 0.85rem;
  }
  .env-badge {
    display: inline-block;
    padding: 0.1rem 0.5rem;
    border-radius: 4px;
    font-size: 0.8rem;
    font-weight: 600;
    text-transform: uppercase;
    background: #edf2f7;
    color: #4a5568;
  }
  .env-badge.production {
    background: #c6f6d5;
    color: #22543d;
  }
  .env-badge.staging {
    background: #feebc8;
    color: #744210;
  }
  .workspace-songs-section {
    margin-bottom: 2rem;
    padding-bottom: 1.5rem;
    border-bottom: 1px solid #e2e8f0;
  }
  .workspace-songs-section:last-child {
    margin-bottom: 0;
    padding-bottom: 0;
    border-bottom: none;
  }
  .workspace-songs-title {
    font-size: 1.1rem;
    font-weight: 600;
    color: #2d3748;
    margin-bottom: 1rem;
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }
  .loading-indicator {
    font-size: 0.9rem;
    color: #7b8794;
    font-weight: normal;
  }
  .song-list {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }
  .song-item {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    padding: 0.75rem;
    background: #f7f9fb;
    border-radius: 8px;
    transition: background-color 0.2s;
  }
  .song-item:hover {
    background: #f1f5f9;
  }
  .song-info {
    flex: 1;
    min-width: 0;
  }
  .song-title {
    font-weight: 500;
    color: #2d3748;
    margin-bottom: 0.25rem;
    font-size: 1rem;
  }
  .song-meta {
    display: flex;
    flex-wrap: wrap;
    gap: 0.75rem;
    color: #7b8794;
    font-size: 0.95rem;
  }
  .song-artist,
  .song-album {
    color: #4a5568;
  }
  .song-duration {
    color: #7b8794;
  }
  .song-date {
    color: #a0aec0;
  }
  .song-uploader {
    font-size: 0.95rem;
    color: #7b8794;
    text-align: right;
    min-width: 140px;
  }
  .uploader-name {
    font-style: italic;
  }
  .song-count {
    font-size: 0.9rem;
    color: #7b8794;
    font-weight: normal;
  }
  .expand-btn {
    margin-left: 1rem;
    background: #f1f5f9;
    color: #2563eb;
    border: none;
    border-radius: 6px;
    padding: 0.3rem 1rem;
    font-size: 0.98rem;
    cursor: pointer;
    transition: background 0.15s;
  }
  .expand-btn:hover {
    background: #e6f0fa;
  }
  .user-actions {
    margin-top: 0.75rem;
  }
  .impersonate-btn {
    background: #2563eb;
    color: #fff;
    border: none;
    border-radius: 6px;
    padding: 0.5rem 1rem;
    font-size: 0.95rem;
    font-weight: 500;
    cursor: pointer;
    transition: background 0.15s;
  }
  .impersonate-btn:hover:not(:disabled) {
    background: #1d4ed8;
  }
  .impersonate-btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  /* Modal: scoped names avoid Bootstrap .modal conflict */
  .modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 9999;
    backdrop-filter: blur(2px);
  }
  .impersonation-modal {
    background: #fff;
    border-radius: 0.5rem;
    padding: 1.5rem;
    width: 100%;
    max-width: 520px;
    position: relative;
    box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
  }
  .impersonation-modal h2 {
    margin: 0 0 0.25rem 0;
    font-size: 1.25rem;
    color: #111827;
  }
  .modal-subtitle {
    color: #6b7280;
    font-size: 0.9rem;
    margin: 0 0 1.25rem 0;
  }
  .modal-close {
    position: absolute;
    top: 0.75rem;
    right: 1rem;
    background: none;
    border: none;
    font-size: 1.5rem;
    cursor: pointer;
    color: #6b7280;
    line-height: 1;
  }
  .modal-close:hover {
    color: #374151;
  }
  .modal-loading {
    padding: 1.5rem 0;
    text-align: center;
    color: #6b7280;
  }
  .modal-error {
    background: #fef2f2;
    border: 1px solid #fecaca;
    color: #dc2626;
    padding: 0.625rem 0.75rem;
    border-radius: 0.375rem;
    font-size: 0.875rem;
    margin-bottom: 1rem;
  }
  .readonly-warning {
    background: #fef3c7;
    border: 1px solid #f59e0b;
    color: #92400e;
    padding: 0.625rem 0.75rem;
    border-radius: 0.375rem;
    font-size: 0.85rem;
    margin-bottom: 1rem;
  }
  .token-label {
    display: block;
    margin-bottom: 0.4rem;
    font-size: 0.8rem;
    font-weight: 500;
    color: #374151;
  }
  .token-field {
    width: 100%;
    padding: 0.625rem 0.75rem;
    border: 1px solid #d1d5db;
    border-radius: 0.375rem;
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    font-size: 0.8rem;
    color: #111827;
    box-sizing: border-box;
    resize: vertical;
    word-break: break-all;
  }
  .token-field:focus {
    outline: none;
    border-color: #2563eb;
    box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.2);
  }
  .modal-actions {
    display: flex;
    justify-content: flex-end;
    gap: 0.75rem;
    margin-top: 1rem;
  }
  .btn-cancel {
    padding: 0.5rem 1rem;
    background: #fff;
    border: 1px solid #d1d5db;
    border-radius: 0.375rem;
    font-size: 0.875rem;
    color: #374151;
    cursor: pointer;
  }
  .btn-cancel:hover {
    background: #f9fafb;
  }
  .btn-submit {
    padding: 0.5rem 1rem;
    background: #2563eb;
    border: none;
    border-radius: 0.375rem;
    font-size: 0.875rem;
    color: #fff;
    cursor: pointer;
    min-width: 120px;
  }
  .btn-submit:hover {
    background: #1d4ed8;
  }

  @media (max-width: 900px) {
    .main-grid {
      grid-template-columns: 1fr;
      gap: 1.5rem;
    }
    .card {
      padding: 1.25rem 0.75rem 1rem 0.75rem;
    }
  }
  @media (max-width: 768px) {
    .user-detail-container {
      padding: 1rem 0.25rem;
    }
    .user-header {
      flex-direction: column;
      align-items: flex-start;
      gap: 1rem;
      margin-bottom: 1.5rem;
    }
    .main-grid {
      grid-template-columns: 1fr;
      gap: 1.5rem;
    }
    .song-item {
      flex-direction: column;
      gap: 0.5rem;
    }
    .song-uploader {
      text-align: left;
      min-width: 0;
    }
  }
</style>
